import React, { useState, useEffect } from 'react';
import { sambutanService } from '../../services/sambutanService';
import {
    FaQuoteLeft, FaQuoteRight, FaUser, FaBuilding,
    FaAward, FaChartLine, FaSpinner
} from 'react-icons/fa';

const Sambutan = () => {
    const [sambutan, setSambutan] = useState(null);
    const [visiMisi, setVisiMisi] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const result = await sambutanService.getPublicSambutan();
        if (result.success) {
            setSambutan(result.data.sambutan);
            setVisiMisi(result.data.visi_misi);
        }
        setLoading(false);
    };

    // ✅ Bersihkan quote dari tanda kutip ganda
    const cleanQuote = (text) => {
        if (!text) return '';
        return String(text).replace(/^["'\s]+|["'\s]+$/g, '').trim();
    };

    // ✅ Bersihkan konten dari karakter aneh
    const cleanContent = (html) => {
        if (!html) return '';
        let cleaned = String(html);
        // Hapus tanda kutip ganda di awal konten
        cleaned = cleaned.replace(/^"+/, '');
        // Hapus escape sequences
        cleaned = cleaned.replace(/\\n/g, '\n');
        // Trim
        cleaned = cleaned.trim();
        return cleaned;
    };

    // ✅ Render konten - deteksi otomatis HTML vs plain text
    const renderContent = (html) => {
        if (!html) return null;

        const cleaned = cleanContent(html);
        if (!cleaned) return null;

        // Cek apakah ada tag HTML block
        const hasHtmlBlockTags = /<(p|h[1-6]|ul|ol|li|blockquote|div|br)\b/i.test(cleaned);

        if (hasHtmlBlockTags) {
            // Konten sudah HTML dari WYSIWYG
            return (
                <div
                    className="sambutan-content"
                    dangerouslySetInnerHTML={{ __html: cleaned }}
                />
            );
        }

        // Konten plain text - split newline jadi paragraf
        const paragraphs = cleaned
            .split(/\n+/)
            .map(p => p.trim())
            .filter(p => p !== '');

        if (paragraphs.length === 0) return null;

        return (
            <div className="sambutan-content">
                {paragraphs.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <section className="py-16 bg-gradient-to-b from-white to-[#F8FAFC]">
                <div className="container mx-auto px-4 text-center">
                    <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto" />
                </div>
            </section>
        );
    }

    if (!sambutan) return null;

    return (
        <section className="py-16 bg-gradient-to-b from-white to-[#F8FAFC]">
            <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                        {/* ============ FOTO & INFO KEPALA DINAS ============ */}
                        <div className="lg:col-span-1">
                            <div className="lg:sticky lg:top-24">
                                <div className="card-glass text-center p-6 md:p-8">
                                    {/* Foto */}
                                    <div className="relative mx-auto w-44 md:w-48 h-52 md:h-56 mb-6">
                                        {sambutan.foto ? (
                                            <img
                                                src={sambutan.foto}
                                                alt={sambutan.nama}
                                                className="w-full h-full object-cover object-top rounded-2xl shadow-2xl border-4 border-white"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    if (e.target.nextSibling) {
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }
                                                }}
                                            />
                                        ) : null}
                                        <div
                                            className="w-full h-full bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] rounded-2xl flex items-center justify-center shadow-2xl"
                                            style={{ display: sambutan.foto ? 'none' : 'flex' }}
                                        >
                                            <FaUser className="text-7xl text-white/80" />
                                        </div>

                                        {/* Badge icon */}
                                        <div className="absolute -bottom-2 -right-2 w-14 h-14 md:w-16 md:h-16 bg-[#2E7D32] rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                                            <FaBuilding className="text-xl md:text-2xl text-white" />
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <h3 className="text-xl md:text-2xl font-bold text-[#0F4C3A] mb-1 leading-tight">
                                        {sambutan.nama}
                                    </h3>
                                    <p className="text-[#2E7D32] font-semibold mb-1 text-sm md:text-base">
                                        {sambutan.jabatan}
                                    </p>
                                    <p className="text-gray-500 text-xs md:text-sm mb-4">
                                        {sambutan.instansi}
                                    </p>
                                    <div className="w-16 h-1 bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] mx-auto rounded-full"></div>
                                </div>
                            </div>
                        </div>

                        {/* ============ SAMBUTAN ============ */}
                        <div className="lg:col-span-2">
                            <div className="card-glass p-4 md:p-8">
                                {/* Label */}
                                <div className="flex items-center mb-6">
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-[#2E7D32]/10 rounded-full flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                                        <FaQuoteLeft className="text-base md:text-xl text-[#2E7D32]" />
                                    </div>
                                    <div>
                                        <span className="text-xs md:text-sm text-[#2E7D32] font-semibold uppercase tracking-wider">
                                            Sambutan
                                        </span>
                                        <h2 className="text-lg md:text-2xl font-bold text-[#0F4C3A]">
                                            Kepala BPKAD Kota Magelang
                                        </h2>
                                    </div>
                                </div>

                                {/* Salam Pembuka */}
                                {sambutan.salam_pembuka && (
                                    <p className="text-base md:text-lg font-bold text-[#0F4C3A] mb-5">
                                        {cleanQuote(sambutan.salam_pembuka)}
                                    </p>
                                )}

                                {/* Konten */}
                                {renderContent(sambutan.konten)}

                                {/* Quote Highlight */}
                                {sambutan.quote_highlight && (
                                    <div className="bg-gradient-to-r from-[#0F4C3A]/5 to-[#2E7D32]/5 p-5 rounded-xl my-6 border-l-4 border-[#2E7D32]">
                                        <p className="font-semibold text-[#0F4C3A] text-sm md:text-base lg:text-lg italic leading-relaxed">
                                            "{cleanQuote(sambutan.quote_highlight)}"
                                        </p>
                                    </div>
                                )}

                                {/* Penutup */}
                                <div className="mt-8 pt-6 border-t border-gray-200">
                                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                                        <div>
                                            <p className="text-gray-600 text-sm mb-2">
                                                Hormat Kami,
                                            </p>
                                            <p className="text-[#0F4C3A] font-bold text-base md:text-lg">
                                                {sambutan.nama}
                                            </p>
                                            <p className="text-gray-500 text-xs md:text-sm">
                                                {sambutan.jabatan}
                                            </p>
                                            <p className="text-gray-500 text-xs md:text-sm">
                                                {sambutan.instansi}
                                            </p>
                                        </div>
                                        {sambutan.nip && (
                                            <div className="bg-[#0F4C3A] text-white px-4 md:px-5 py-2 md:py-3 rounded-lg inline-block self-start md:self-auto">
                                                <p className="text-xs opacity-80">NIP.</p>
                                                <p className="font-mono text-xs md:text-sm">
                                                    {sambutan.nip}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <FaQuoteRight className="text-3xl md:text-4xl text-[#2E7D32]/20 mt-6 ml-auto" />
                            </div>

                            {/* ============ VISI MISI ============ */}
                            {visiMisi && (
                                <div className="flex flex-col gap-6 mt-8">
                                    {/* VISI */}
                                    <div className="card-glass p-6 bg-gradient-to-br from-[#0F4C3A] to-[#0F4C3A]/90 text-white">
                                        <h4 className="text-base md:text-lg font-bold mb-3 flex items-center">
                                            <FaAward className="mr-2 flex-shrink-0" /> VISI
                                        </h4>
                                        <p className="text-sm leading-relaxed opacity-95">
                                            "{cleanQuote(visiMisi.visi)}"
                                        </p>
                                        {visiMisi.periode && (
                                            <p className="text-xs opacity-70 mt-3">
                                                Periode {visiMisi.periode}
                                            </p>
                                        )}
                                    </div>

                                    {/* MISI */}
                                    <div className="card-glass p-6 bg-gradient-to-br from-[#2E7D32] to-[#2E7D32]/90 text-white">
                                        <h4 className="text-base md:text-lg font-bold mb-3 flex items-center">
                                            <FaChartLine className="mr-2 flex-shrink-0" /> MISI
                                        </h4>
                                        <ul className="text-sm space-y-2 opacity-95">
                                            {visiMisi.misi && visiMisi.misi.map((m, i) => (
                                                <li key={i} className="flex items-start">
                                                    <span className="leading-relaxed">{m}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Sambutan;